import { requireFilteredPaymentIdentity } from './payment_identity';
import { useEffect, useRef, useState } from 'react';
import type { z } from 'zod';
import { authFetch } from '../../utils/api';

/** Preserve structured errors so financial actions can distinguish a known rejection from uncertain delivery. */
export class SubscriptionRequestError extends Error {
  constructor(message: string, public readonly status: number, public readonly errorCode: string | null, public readonly data: unknown) {
    super(message);
    this.name = 'SubscriptionRequestError';
  }
}
export async function subscriptionRequest<T>(path: string, schema: z.ZodType<T, z.ZodTypeDef, unknown>, options?: RequestInit): Promise<T> {
  const response = await authFetch(path, options);
  const result = await response.json();
  if (!response.ok || result.code !== 0) throw new SubscriptionRequestError(result.msg || `請求失敗 (${response.status})`, response.status, typeof result.data?.errorCode === 'string' ? result.data.errorCode : null, result.data);
  requireFilteredPaymentIdentity(path, result.data);
  const parsed = schema.safeParse(result.data);
  if (!parsed.success) throw new Error('伺服器資料不完整，請重新整理或聯絡管理員');
  return parsed.data;
}
export function readPath(path: string, brand: string, query: Record<string, string | undefined> = {}) {
  if (!brand || brand === 'all') throw new Error('請選擇單一品牌');
  const params = new URLSearchParams({ brand });
  for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
  return `${path}?${params}`;
}
/** The plan catalog accepts at most 50 rows per page. */
export function planDraftReadbackPath(brand: string, productId: string, cursor?: string) {
  return readPath('/api/admin/subscriptions/plans', brand, { productId, cursor, limit: '50' });
}
export function writeOptions(targetBrand: string, values: object, method = 'POST'): RequestInit {
  if (!targetBrand || targetBrand === 'all') throw new Error('請明確指定目標品牌');
  return { method, headers: { 'X-Target-Brand': targetBrand }, body: JSON.stringify({ ...values, targetBrand }) };
}
/** Retry an identical draft request with its original key; edits start a new request. */
export function useDraftIdempotency() {
  const request = useRef<{ values: string; key: string } | null>(null);
  return (values: object) => {
    const serialized = JSON.stringify(values);
    if (request.current?.values !== serialized) request.current = { values: serialized, key: crypto.randomUUID() };
    return request.current.key;
  };
}
export function useSubscriptionRead<T>(path: string | null, schema: z.ZodType<T, z.ZodTypeDef, unknown>, revision = 0) {
  const key = `${path}:${revision}`;
  const [state, setState] = useState<{ key: string; data: T | null; error: string | null; loading: boolean }>();
  useEffect(() => {
    if (!path) return;
    const controller = new AbortController();
    setState({ key, data: null, error: null, loading: true });
    subscriptionRequest(path, schema, { signal: controller.signal }).then(data => {
      if (!controller.signal.aborted) setState({ key, data, error: null, loading: false });
    }).catch(error => {
      if (!controller.signal.aborted) setState({ key, data: null, error: error instanceof Error ? error.message : '讀取失敗', loading: false });
    });
    return () => controller.abort();
  }, [path, schema, key]);
  return state?.key === key ? state : { data: null, error: null, loading: !!path };
}
